// oxlint-disable complexity
import type { QueryClient } from '@tanstack/react-query';

import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import { localStorage } from '@change-corgi/core/storage';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDraft } from 'src/app/shared/api/petitionDraft';
import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from 'src/app/shared/api/petitionDraft';

import { getDraftUserData } from './getDraftUserData';

type UtilityErrorReporter = Pick<ErrorReporter, 'report'>;
type FindOrCreateDraftOptions = {
	errorReporter: UtilityErrorReporter;
	gqlClient: UtilityContext['gql'];
	queryClient: QueryClient;
};
type Experiments = Awaited<ReturnType<typeof getDraftUserData>>['experiments'];
type Viewer = Awaited<ReturnType<typeof getDraftUserData>>['viewer'];

function getVariant(experiments: Experiments) {
	if (experiments.starfishPreviewEditExperiment.variation === 'variant_a') return 'preview_edit_variant_a' as const;
	if (experiments.starfishPreviewEditExperiment.variation === 'variant_b') return 'preview_edit_variant_b' as const;
	if (experiments.starfishSap3RecommendedAngles.variation === 'variant_b') return 'recommendations_variant_b' as const;
	return 'control' as const;
}

function getLoginState(viewer: Viewer) {
	return viewer.user?.id ? 'AUTHENTICATED' : 'GUEST';
}

export async function findOrCreateDraft(options: FindOrCreateDraftOptions): Promise<PetitionDraft> {
	try {
		const draftClient = new DraftClient([
			new LocalStorageAdapter(localStorage),
			new InMemoryAdapter(options.queryClient),
		]);

		// get user data
		const { experiments, viewer } = await getDraftUserData({
			fetch: options.gqlClient.fetch,
			queryClient: options.queryClient,
		});

		// find or create draft
		const draft = await draftClient.findOrCreateDraft(viewer.draftAuthorId);

		const variant = getVariant(experiments);
		const loginState = getLoginState(viewer);

		// updated metadata values
		const defaultMetadata = { user: { id: viewer.user?.id, initialLoginState: loginState }, variant };

		// reset draft if previous variant
		const didVariantChange = draft.metadata?.variant && draft.metadata.variant !== variant;
		if (didVariantChange) {
			return draftClient.createDraft(viewer.draftAuthorId, { metadata: defaultMetadata });
		}

		// reset draft if current authenticated user id does not match the stored user id
		const didUserChange = draft.metadata?.user?.id && viewer.user?.id !== draft.metadata.user.id;
		if (didUserChange) {
			return draftClient.createDraft(viewer.draftAuthorId, { metadata: defaultMetadata });
		}

		// merge metadata with current user.id and prior initialLoginState if it exists (to prevent overwriting with guest state on variant change)
		const metadata = {
			...draft.metadata,
			...defaultMetadata,
			user: {
				...draft.metadata?.user,
				id: defaultMetadata.user.id,
				initialLoginState: draft.metadata?.user?.initialLoginState ?? defaultMetadata.user.initialLoginState,
			},
		};

		return { ...draft, metadata };
	} catch (error) {
		// report and throw
		throw new Error(`Failed to find or create draft: ${(error as Error).message}`, { cause: error });
	}
}
